import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";
import {
  getCategoryById,
  getCategoryMain,
} from "../../service/Category/categoryService";
import {
  CategorySubCategoryRow,
  mapSubCategoriesForTable,
} from "../../Pages/Category/Utils/categoryMappers";

export const CATEGORY_CACHE_TTL_MS = 5 * 60 * 1000;

export type FetchCategoryOptions = {
  force?: boolean;
};

export type FetchCategorySubcategoriesOptions = {
  categoryId: number;
  force?: boolean;
};

type SubCategoryCacheEntry = {
  items: CategorySubCategoryRow[];
  loading: boolean;
  error: string | null;
  fetchedAt: number | null;
};

interface CategoryState {
  categories: any[];
  loading: boolean;
  error: string | null;
  selectedState: string;
  lastFetchedAt: number | null;
  subCategoriesByParentId: Record<string, SubCategoryCacheEntry>;
}

const initialState: CategoryState = {
  categories: [],
  loading: false,
  error: null,
  selectedState: "",
  lastFetchedAt: null,
  subCategoriesByParentId: {},
};

const emptySubCacheEntry = (): SubCategoryCacheEntry => ({
  items: [],
  loading: false,
  error: null,
  fetchedAt: null,
});

const ensureSubCategoryCache = (state: CategoryState) => {
  if (!state.subCategoriesByParentId) {
    state.subCategoriesByParentId = {};
  }
};

const getRehydratedCategoryState = (
  action: unknown
): CategoryState | undefined => {
  const payload = (action as { payload?: { category?: CategoryState } | null })
    .payload;
  return payload?.category;
};

export const fetchcategory = createAsyncThunk<
  any[],
  FetchCategoryOptions | void,
  { rejectValue: string }
>(
  "category/fetchcategory",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCategoryMain();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch categories");
    }
  },
  {
    condition: (arg, { getState }) => {
      const force = (arg as FetchCategoryOptions | undefined)?.force ?? false;
      if (force) {
        return true;
      }

      const state = (getState() as { category: CategoryState }).category;
      if (!state.lastFetchedAt || state.categories.length === 0) {
        return true;
      }

      return Date.now() - state.lastFetchedAt >= CATEGORY_CACHE_TTL_MS;
    },
  }
);

export const fetchCategorySubcategories = createAsyncThunk<
  { categoryId: number; items: CategorySubCategoryRow[] },
  FetchCategorySubcategoriesOptions,
  { rejectValue: { categoryId: number; message: string } }
>(
  "category/fetchCategorySubcategories",
  async ({ categoryId }, { rejectWithValue }) => {
    try {
      const response = await getCategoryById(categoryId);
      const items = mapSubCategoriesForTable(
        response.data?.data?.categories,
        categoryId
      );
      return { categoryId, items };
    } catch (error: any) {
      return rejectWithValue({
        categoryId,
        message: error.response?.data?.message || "Failed to fetch subcategories",
      });
    }
  },
  {
    condition: (arg, { getState }) => {
      const force = arg.force ?? false;
      if (force) {
        return true;
      }

      const key = String(arg.categoryId);
      const entry = (getState() as { category: CategoryState }).category
        .subCategoriesByParentId?.[key];

      if (!entry?.fetchedAt) {
        return true;
      }

      return Date.now() - entry.fetchedAt >= CATEGORY_CACHE_TTL_MS;
    },
  }
);

const categorySlice = createSlice({
  name: "category",
  initialState,
  reducers: {
    setCategoryState: (state, action: PayloadAction<string>) => {
      state.selectedState = action.payload;
    },
    invalidateCategoryCache: (state) => {
      state.lastFetchedAt = null;
      ensureSubCategoryCache(state);
      state.subCategoriesByParentId = {};
    },
    invalidateSubCategoriesForParent: (state, action: PayloadAction<number>) => {
      ensureSubCategoryCache(state);
      const key = String(action.payload);
      delete state.subCategoriesByParentId[key];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(REHYDRATE, (state, action) => {
        const incoming = getRehydratedCategoryState(action);
        if (!incoming) {
          return;
        }

        state.categories = incoming.categories ?? [];
        state.loading = incoming.loading ?? false;
        state.error = incoming.error ?? null;
        state.selectedState = incoming.selectedState ?? "";
        state.lastFetchedAt = incoming.lastFetchedAt ?? null;
        state.subCategoriesByParentId = incoming.subCategoriesByParentId ?? {};
      })
      .addCase(fetchcategory.pending, (state, action) => {
        ensureSubCategoryCache(state);
        state.loading = true;
        state.error = null;
        if ((action.meta.arg as FetchCategoryOptions | undefined)?.force) {
          state.subCategoriesByParentId = {};
        }
      })
      .addCase(fetchcategory.fulfilled, (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.categories = action.payload;
        state.lastFetchedAt = Date.now();
      })
      .addCase(fetchcategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchCategorySubcategories.pending, (state, action) => {
        ensureSubCategoryCache(state);
        const key = String(action.meta.arg.categoryId);
        const existing = state.subCategoriesByParentId[key] ?? emptySubCacheEntry();
        state.subCategoriesByParentId[key] = {
          ...existing,
          loading: true,
          error: null,
        };
      })
      .addCase(fetchCategorySubcategories.fulfilled, (state, action) => {
        ensureSubCategoryCache(state);
        const key = String(action.payload.categoryId);
        state.subCategoriesByParentId[key] = {
          items: action.payload.items,
          loading: false,
          error: null,
          fetchedAt: Date.now(),
        };
      })
      .addCase(fetchCategorySubcategories.rejected, (state, action) => {
        ensureSubCategoryCache(state);
        const categoryId = action.payload?.categoryId ?? action.meta.arg.categoryId;
        const key = String(categoryId);
        const existing = state.subCategoriesByParentId[key] ?? emptySubCacheEntry();
        state.subCategoriesByParentId[key] = {
          ...existing,
          loading: false,
          error: action.payload?.message || "Failed to fetch subcategories",
        };
      });
  },
});

export const {
  setCategoryState,
  invalidateCategoryCache,
  invalidateSubCategoriesForParent,
} = categorySlice.actions;
export default categorySlice.reducer;
